<template>
    <div id="welcome">
        <!-- Topbar carries the mobile-browser nav hamburger; hidden on desktop
             and in-app (see base/06_mobile.css) so the hero stays clean there. -->
        <CanvasTopbar class="zs-topbar-mobile" />

        <div class="welcome__hero">
            <span class="welcome__wave" role="img" aria-label="Waving hand">👋</span>
            <h4 class="welcome__title">Welcome to <span class="welcome__brand">Zyno</span><span class="welcome__brand-strong">Suite</span></h4>

            <section v-if="showTapToPayAd" class="welcome__t2p" aria-label="Tap to Pay on iPhone">
                <button
                    v-tooltip="'Dismiss'"
                    type="button"
                    class="zs-btn zs-btn--icon zs-btn--sm welcome__t2p-dismiss"
                    aria-label="Dismiss"
                    @click="dismissTapToPayAd"
                >
                    <i class="fa-solid fa-xmark" />
                </button>
                <div class="welcome__t2p-body">
                    <h5 class="welcome__t2p-title">Tap to Pay on iPhone</h5>
                    <p class="welcome__t2p-text">
                        Accept contactless cards, Apple Pay, and other digital wallets right on your iPhone — no extra hardware. Just choose
                        <strong>Tap to Pay</strong> when you take a payment.
                    </p>
                    <button type="button" class="zs-btn zs-btn--primary welcome__t2p-cta" :disabled="settingUpTapToPay" @click="setUpTapToPay">
                        {{ settingUpTapToPay ? 'Setting up...' : 'Set up Tap to Pay' }}
                    </button>
                </div>
            </section>

            <nav v-if="tiles.length" class="welcome__apps">
                <template v-for="tile in tiles" :key="tile.key">
                    <RouterLink v-if="tile.to" :to="tile.to" class="welcome__app">
                        <span v-if="tile.badge" class="zs-nav-dot welcome__app-dot" :title="tile.badgeTitle" />
                        <i :class="`${tile.icon} welcome__app-icon`" />
                        <span class="welcome__app-name">{{ tile.name }}</span>
                    </RouterLink>
                    <button v-else type="button" class="welcome__app" @click="tile.onClick?.()">
                        <span v-if="tile.badge" class="zs-nav-dot welcome__app-dot" :title="tile.badgeTitle" />
                        <i :class="`${tile.icon} welcome__app-icon`" />
                        <span class="welcome__app-name">{{ tile.name }}</span>
                    </button>
                </template>
            </nav>

            <p class="welcome__help">
                Need a hand? <a href="#" @click.prevent="openSupport">Open a support ticket</a> and we'll be in touch shortly.
            </p>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { handleErrorAndAlert, showAlert } from '@zyno-io/vue-foundation';
import { computed, ref } from 'vue';
import { RouterLink } from 'vue-router';

import { MobileBridge } from '@/modules/mobile/helper/bridge';
import { canAccessFullSalespoint } from '@/modules/sales/subapps/salespoint/permissions';
import { launchZynoTalk, showZynoTalkLauncher } from '@/modules/talk/zynotalk-launcher';
import CanvasTopbar from '@/shared/components/v2/canvas-topbar.vue';
import { useIsMobileReady } from '@/shared/helpers/app.helpers';
import { openHelp } from '@/shared/helpers/help.helpers';

import { useAuthorizedLauncherItems } from './exports/launcher-items';

useIsMobileReady();

// Persist a manual dismissal locally so the nudge stays gone once the user waves
// it off (mirrors the acknowledged-incident pattern in exports/incidents.ts).
const TTP_AD_DISMISSED_KEY = 'zs:dismissedTapToPayAd';
const tapToPayAdDismissed = ref(localStorage.getItem(TTP_AD_DISMISSED_KEY) === '1');
function dismissTapToPayAd() {
    tapToPayAdDismissed.value = true;
    localStorage.setItem(TTP_AD_DISMISSED_KEY, '1');
}

// Apple mandates an in-app awareness moment for Tap to Pay on iPhone. Show it on
// the launcher when running in the native app on a capable device, for users who
// can actually take payments (Salespoint) — until they've been through the
// education sheet or dismissed it, after which the nudge is no longer needed.
const showTapToPayAd = computed(
    () =>
        !tapToPayAdDismissed.value &&
        MobileBridge.isWithinZynoSuiteMobile &&
        !!MobileBridge.config.value?.isTapToPayCapable &&
        !MobileBridge.config.value?.hasDisplayedTTPEducation &&
        MobileBridge.config.value?.platform === 'ios' &&
        canAccessFullSalespoint.value
);

const settingUpTapToPay = ref(false);
async function setUpTapToPay() {
    if (settingUpTapToPay.value) return;
    settingUpTapToPay.value = true;
    try {
        const { activated } = await MobileBridge.sendRequest('activateTapToPay', undefined);
        // Activation presents Apple's education sheet; reflect that locally so the
        // card dismisses now rather than lingering until the next config load.
        if (activated && MobileBridge.config.value) MobileBridge.config.value.hasDisplayedTTPEducation = true;
        showAlert('Tap to Pay has been activated! When you collect a payment in Salespoint, just choose the "Tap to Pay on iPhone" option.');
    } catch (err) {
        handleErrorAndAlert(err);
    } finally {
        settingUpTapToPay.value = false;
    }
}

const launcherItems = useAuthorizedLauncherItems();

// A launcher tile is either a route (RouterLink) or an action (button) — the ZynoTalk hand-off is
// the only action tile today.
interface IWelcomeTile {
    key: string;
    name: string;
    icon: string;
    to?: string;
    onClick?: () => void;
    /** "Something in here needs you" — the same dot the sidebar entry carries. */
    badge?: boolean;
    badgeTitle?: string;
}

// Talk has no usable mobile web surface, so for Talk-entitled tenants we morph its launcher tile —
// in place, keeping its natural order — into a hand-off to the standalone ZynoTalk app or its store
// (via the native bridge in the app, or the deep-link-then-store trick in a phone browser). All of
// that lives in the launcher module.
const showTalkAppTile = computed(() => showZynoTalkLauncher());

const tiles = computed<IWelcomeTile[]>(() =>
    launcherItems.value
        .map((item): IWelcomeTile | null => {
            // Morph Talk in place into the app/store hand-off, overriding its route — and its mobile
            // `hidden` flag, since the native-app routing tile is otherwise hidden by launcher-items.
            // Evaluated here, inside the computed, so the dot tracks whatever reactive state the
            // item's predicate reads (see launcher-items.ts).
            const badge = item.badge?.() ?? false;
            if (item.path === '/talk' && showTalkAppTile.value) {
                return { key: item.path, name: item.name, icon: item.icon, onClick: launchZynoTalk, badge, badgeTitle: item.badgeTitle };
            }
            if (item.hidden) return null;
            // Apps with no direct route (path '#') link to their first accessible sub-app.
            const to = item.path !== '#' ? item.path : (item.subItems?.find(s => !s.hidden)?.path ?? item.path);
            return to === '#' ? null : { key: item.path, name: item.name, icon: item.icon, to, badge, badgeTitle: item.badgeTitle };
        })
        .filter((tile): tile is IWelcomeTile => tile !== null)
);

function openSupport() {
    openHelp();
}
</script>

<style scoped>
#welcome {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.welcome__hero {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--s-6);
    padding: var(--s-6);
}

.welcome__wave {
    font-size: 48px;
    line-height: 1;
    transform-origin: 70% 80%;
    animation: welcome-wave 2.5s ease-in-out infinite;
}

.welcome__title {
    font-family: var(--font-display);
    font-size: var(--t-xl);
    /* "Welcome to" normal, "Zyno" extralight, "Suite" bold — mirrors the sidebar wordmark. */
    font-weight: var(--w-reg);
    letter-spacing: -0.005em;
    color: var(--ink);
    margin: 0;
}

.welcome__brand {
    font-weight: 200;
}

.welcome__brand-strong {
    font-weight: var(--w-med);
}

.welcome__t2p {
    position: relative;
    width: 100%;
    max-width: 560px;
    padding: var(--s-5);
    border: 1px solid var(--accent-tint);
    border-radius: var(--r-3);
    background: var(--accent-soft);
    text-align: left;
}

.welcome__t2p-dismiss {
    position: absolute;
    top: var(--s-2);
    right: var(--s-2);
    color: var(--ink-3);
}

.welcome__t2p-body {
    display: flex;
    flex-direction: column;
    gap: var(--s-2);
    min-width: 0;
}

.welcome__t2p-title {
    margin: 0;
    font-size: var(--t-md);
    font-weight: var(--w-med);
    color: var(--ink);
}

.welcome__t2p-text {
    margin: 0;
    font-size: var(--t-sm);
    line-height: var(--lh-snug);
    color: var(--ink-2);
}

.welcome__t2p-text strong {
    font-weight: var(--w-med);
    color: var(--ink);
}

.welcome__t2p-cta {
    align-self: flex-start;
    margin-top: var(--s-1);
}

.welcome__apps {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(96px, 104px));
    gap: var(--s-3);
    justify-content: center;
    max-width: 560px;
}

.welcome__app {
    /* Anchors the attention dot in the corner. */
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--s-2);
    min-height: 104px;
    padding: var(--s-4) var(--s-2);
    border: 1px solid var(--line);
    border-radius: var(--r-3);
    background: var(--surface);
    color: var(--ink-2);
    text-decoration: none;
    /* Shared by <a> route tiles and <button> action tiles; neutralize button UA styling. */
    appearance: none;
    width: 100%;
    font: inherit;
    text-align: center;
    cursor: pointer;
    transition:
        border-color 100ms ease,
        box-shadow 100ms ease,
        transform 100ms ease,
        color 100ms ease;
}

/* Tucked into the tile's corner rather than beside the label: the tile centres its icon and name,
   and anything in that column shifts them off-centre. */
.welcome__app-dot {
    position: absolute;
    top: var(--s-2);
    right: var(--s-2);
}

.welcome__app:hover {
    border-color: var(--accent);
    box-shadow: var(--el-2);
    color: var(--accent-ink);
    transform: translateY(-2px);
}

.welcome__app-icon {
    font-size: 22px;
    color: var(--accent);
}

.welcome__app-name {
    width: 100%;
    font-size: var(--t-sm);
    font-weight: var(--w-med);
    line-height: var(--lh-tight);
    text-align: center;
    overflow-wrap: anywhere;
}

.welcome__help {
    font-size: var(--t-sm);
    color: var(--ink-3);
    margin: 0;
    text-align: center;
}

.welcome__help a {
    color: var(--accent);
}

.welcome__help a:hover {
    color: var(--accent-ink);
}

@media (max-width: 768px) {
    .welcome__hero {
        /* Top-align so the grid isn't buried under the keyboard / short
           viewports, and pad clear of the bottom safe area. */
        justify-content: flex-start;
        gap: var(--s-4);
        padding: var(--s-6) var(--s-4) calc(var(--s-6) + var(--zs-safe-bottom, 0px));
    }

    .welcome__wave {
        font-size: 40px;
        margin-top: var(--s-4);
    }

    .welcome__apps {
        /* Fill the row with as many tiles as fit instead of fixed 104px cols. */
        grid-template-columns: repeat(auto-fit, minmax(96px, 1fr));
        width: 100%;
        max-width: 420px;
    }
}

@keyframes welcome-wave {
    0% {
        transform: rotate(0deg);
    }
    10% {
        transform: rotate(15deg);
    }
    20% {
        transform: rotate(-15deg);
    }
    30% {
        transform: rotate(15deg);
    }
    40% {
        transform: rotate(-15deg);
    }
    50% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(0deg);
    }
}
</style>
